/**
 * PURPOSE: Aviso de partidos externos al Centro de Programación (FTR-0028 WG3, §16 del encargo; D-34).
 * RESPONSIBILITIES: Decir que existen partidos de otras herramientas en este evento y deporte,
 *   mostrarlos en solo lectura y señalar los que comparten coordenada con una fila del Centro.
 *
 *   NI SE OCULTAN NI BLOQUEAN. Un partido externo —del generador antiguo, del planificador, de
 *   cualquier otra pantalla— no es un error: es trabajo de alguien. El Centro no lo modifica nunca
 *   (no tiene `match_key`), pero tampoco se calla que existe ni se niega a trabajar por su causa.
 *
 *   UN DUPLICADO ES UN AVISO, NO UN VEREDICTO. Que un partido externo ocupe la misma coordenada
 *   estructural `(grupo, fecha, equipo1, equipo2)` que una fila propia suele significar que uno de
 *   los dos sobra — pero cuál, lo sabe el administrador, no el sistema.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, services/programmingCenterService.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/components/ExternalMatchesBanner.jsx
 */
import React, { useState } from "react";
import { FiChevronDown, FiChevronUp, FiEyeOff, FiLayers } from "react-icons/fi";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { groupLetter } from "../../../../utils/programmingCenterModel.js";

const ExternalMatchesBanner = () => {
  const { context, ownership } = useProgrammingCenter();
  const [open, setOpen] = useState(false);

  const counts = ownership?.counts || context?.matches || null;
  const external = Number(counts?.external) || 0;
  if (!external) return null;

  const duplicates = ownership?.duplicates || [];
  const rows = ownership?.external || [];

  return (
    <section className="border-b border-amber-200 bg-amber-50 px-4 py-2 print:hidden">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-black text-amber-900">
          <FiLayers className="text-sm" />
          Existen {external} partidos externos al Centro de Programación
          {duplicates.length ? (
            <span className="rounded-full border border-amber-400 bg-white px-2 py-0.5 text-[11px] font-black text-amber-800">
              {duplicates.length} coinciden con una fila propia
            </span>
          ) : null}
        </p>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2 py-1 text-xs font-black text-amber-800 transition hover:bg-amber-100"
        >
          {open ? <FiChevronUp className="text-xs" /> : <FiChevronDown className="text-xs" />}
          {open ? "Ocultar" : "Ver detalle"}
        </button>
      </div>

      <p className="mt-1 flex items-center gap-1.5 text-[11px] font-bold text-amber-800">
        <FiEyeOff className="text-xs" />
        El Centro no los modifica: fueron creados por otra herramienta y se muestran solo para que
        puedas detectar duplicados.
      </p>

      {open ? (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-amber-200 bg-white">
          <table className="w-full table-auto text-left text-xs">
            <thead className="sticky top-0 bg-amber-50">
              <tr>
                {["N°", "Fase", "Grupo", "Fecha", "Equipos", "Programación", "Aviso"].map(
                  (column) => (
                    <th
                      key={column}
                      className="px-2 py-1.5 text-[10px] font-black uppercase tracking-wide text-amber-700"
                    >
                      {column}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-amber-100">
              {rows.map((match) => {
                const duplicate = duplicates.find((item) => item.idmatch === match.idmatch);
                return (
                  <tr key={match.idmatch} className={duplicate ? "bg-amber-50/70" : ""}>
                    <td className="px-2 py-1.5 font-black text-slate-600">
                      {match.nro_match ? `#${match.nro_match}` : "—"}
                    </td>
                    <td className="px-2 py-1.5 font-bold text-slate-600">
                      {Number(match.idtypephase) === 2 ? "Final" : "Grupos"}
                    </td>
                    <td className="px-2 py-1.5 font-bold text-slate-600">
                      {match.idgrupo ? groupLetter(match.idgrupo) : "—"}
                    </td>
                    <td className="px-2 py-1.5 font-bold text-slate-600">{match.nro_fecha || "—"}</td>
                    <td className="px-2 py-1.5 font-bold text-slate-700">
                      {match.flag_bye
                        ? `${match.nro_team1 ?? "?"} descansa`
                        : `${match.nro_team1 ?? "?"} vs ${match.nro_team2 ?? "?"}`}
                    </td>
                    <td className="px-2 py-1.5 font-bold text-slate-600">
                      {[match.date_match, match.time_match?.slice(0, 5)].filter(Boolean).join(" ") ||
                        "sin programar"}
                    </td>
                    <td className="px-2 py-1.5 font-black text-amber-800">
                      {duplicate ? `Duplica ${duplicate.collidesWith.join(", ")}` : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
};

export default ExternalMatchesBanner;
